import { Head } from 'vike-react/Head'
import { useConfig } from 'vike-react/useConfig'
import type { Product, Variant } from '../../types'
import { supportedLanguages } from '../../i18n'
import {
  canonicalUrl,
  metaDescription,
  pageTitle,
  productJsonLd,
  breadcrumbJsonLd,
  variantAlternates,
} from './seo'

export interface ProductSeoHeadProps {
  lang: string
  product: Product
  variant: Variant | null
  variants: readonly Variant[]
  /** True when rendering `/product/{slug}` (no variant segment in URL). */
  isCanonicalPage: boolean
}

/**
 * Renders everything the crawler needs in <head>, in the initial server HTML:
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical"> (self-referential per variant URL, per DEC-016)
 *   - <link rel="alternate"> for every sibling variant URL
 *   - Product+Offer JSON-LD (one block per variant URL)
 *   - BreadcrumbList JSON-LD
 *
 * Split out so it can be unit-tested without mounting <Head> (which requires a
 * Vike page context).
 */
export default function ProductSeoHead({
  lang,
  product,
  variant,
  variants,
  isCanonicalPage,
}: ProductSeoHeadProps) {
  const title = pageTitle(product, variant)
  const description = metaDescription(product, variant)
  const configure = useConfig()
  configure({ title, description, lang })

  // When we're on the canonical `/product/{slug}` URL, canonical is also
  // `/product/{slug}` (no variant segment). When on a variant URL, canonical
  // is self-referential per DEC-016.
  const canonical = canonicalUrl(lang, product.slug, isCanonicalPage ? undefined : variant?.slug)

  const productLd = productJsonLd(lang, product, variant, { isCanonicalPage })
  const breadcrumbLd = breadcrumbJsonLd(lang, product, variant)

  const alternates = variantAlternates(lang, product.slug, variants)

  const heroImage = (variant ?? product).primary_image_url

  return (
    <Head>
      {heroImage && (
        <link rel="preload" as="image" href={heroImage} fetchPriority="high" />
      )}
      <link rel="canonical" href={canonical} />
      {/* hreflang for the same product across supported locales — lets Google
          serve /nl/... to Dutch readers and /en/... to English readers. */}
      {supportedLanguages.map((lng) => (
        <link
          key={`hreflang-${lng}`}
          rel="alternate"
          hrefLang={lng}
          href={canonicalUrl(lng, product.slug, isCanonicalPage ? undefined : variant?.slug)}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={canonicalUrl('nl', product.slug, isCanonicalPage ? undefined : variant?.slug)}
      />
      {/* Sibling variants — helps crawlers see this is a product family.
          Per DEC-016, uses rel="alternate" (not hreflang). */}
      {alternates.map((a) => (
        <link
          key={a.variant.uuid}
          rel="alternate"
          href={a.url}
          data-variant-slug={a.variant.slug}
        />
      ))}
      <meta property="og:type" content="product" />
      <meta property="og:url" content={canonical} />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productLd) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />
    </Head>
  )
}
